/**
 * Website cost calculator — pure logic, shared by the server-rendered page, the client
 * widget and the embed, so a shared URL always renders the same numbers everywhere.
 * Prices are in EGP; SAR / AED use the editable exchange rates in the config.
 */

export const SITE_TYPES = ['landing', 'company', 'ecommerce', 'webapp'] as const;
export type SiteType = (typeof SITE_TYPES)[number];
export type Currency = 'EGP' | 'SAR' | 'AED';

export interface CalculatorInputs {
  type: SiteType;
  pages: number;
  languages: number;
  /** Online store (cart + checkout). Always true for the "ecommerce" site type. */
  ecommerce: boolean;
  integrations: number;
}

export interface CalculatorConfig {
  basePrices: Record<SiteType, number>;
  includedPages: Record<SiteType, number>;
  extraPagePrice: number;
  extraLanguagePercent: number;
  ecommerceAddon: number;
  integrationPrice: number;
  rangeLow: number;
  rangeHigh: number;
  rates: { SAR: number; AED: number };
}

/** Starting point, used when the CMS has no pricing (or a field is missing). EGP. */
export const DEFAULT_CONFIG: CalculatorConfig = {
  basePrices: { landing: 8_000, company: 20_000, ecommerce: 50_000, webapp: 80_000 },
  includedPages: { landing: 1, company: 5, ecommerce: 8, webapp: 6 },
  extraPagePrice: 1_500,
  extraLanguagePercent: 25,
  ecommerceAddon: 25_000,
  integrationPrice: 4_000,
  rangeLow: 0.85,
  rangeHigh: 1.35,
  rates: { SAR: 0.075, AED: 0.074 },
};

export const LIMITS = {
  pages: { min: 1, max: 100 },
  languages: { min: 1, max: 4 },
  integrations: { min: 0, max: 15 },
} as const;

export const DEFAULT_INPUTS: CalculatorInputs = {
  type: 'company',
  pages: 6,
  languages: 2,
  ecommerce: false,
  integrations: 1,
};

// ── Config ───────────────────────────────────────────────────────────────────

const num = (value: unknown, fallback: number, min = 0): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n >= min ? n : fallback;
};

/** Deep-merges CMS pricing over the defaults, ignoring anything that isn't a sane number. */
export function mergeConfig(cms: unknown): CalculatorConfig {
  const c = (cms && typeof cms === 'object' ? cms : {}) as Record<string, any>;
  const perType = (key: 'basePrices' | 'includedPages') =>
    Object.fromEntries(
      SITE_TYPES.map((t) => [t, num(c[key]?.[t], DEFAULT_CONFIG[key][t])]),
    ) as Record<SiteType, number>;

  const low = num(c.rangeLow, DEFAULT_CONFIG.rangeLow, 0.1);
  const high = num(c.rangeHigh, DEFAULT_CONFIG.rangeHigh, 1);
  return {
    basePrices: perType('basePrices'),
    includedPages: perType('includedPages'),
    extraPagePrice: num(c.extraPagePrice, DEFAULT_CONFIG.extraPagePrice),
    extraLanguagePercent: num(c.extraLanguagePercent, DEFAULT_CONFIG.extraLanguagePercent),
    ecommerceAddon: num(c.ecommerceAddon, DEFAULT_CONFIG.ecommerceAddon),
    integrationPrice: num(c.integrationPrice, DEFAULT_CONFIG.integrationPrice),
    rangeLow: Math.min(low, 1),
    rangeHigh: Math.max(high, 1),
    rates: {
      SAR: num(c.rates?.SAR, DEFAULT_CONFIG.rates.SAR, 0.0001),
      AED: num(c.rates?.AED, DEFAULT_CONFIG.rates.AED, 0.0001),
    },
  };
}

// ── Inputs ───────────────────────────────────────────────────────────────────

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const first = (v: string | string[] | undefined) => (Array.isArray(v) ? v[0] : v);
const int = (v: string | string[] | undefined, fallback: number, min: number, max: number) => {
  const n = Number.parseInt(first(v) ?? '', 10);
  return Number.isFinite(n) ? clamp(n, min, max) : fallback;
};

/** Reads (untrusted) query params into valid inputs; anything missing or invalid gets the default. */
export function parseInputs(
  params: Record<string, string | string[] | undefined> = {},
): CalculatorInputs {
  const rawType = first(params.type);
  const type = (SITE_TYPES as readonly string[]).includes(rawType ?? '')
    ? (rawType as SiteType)
    : DEFAULT_INPUTS.type;
  const store = first(params.store);
  return {
    type,
    pages: int(params.pages, DEFAULT_INPUTS.pages, LIMITS.pages.min, LIMITS.pages.max),
    languages: int(params.langs, DEFAULT_INPUTS.languages, LIMITS.languages.min, LIMITS.languages.max),
    ecommerce: type === 'ecommerce' ? true : store === undefined ? DEFAULT_INPUTS.ecommerce : store === '1',
    integrations: int(
      params.integrations,
      DEFAULT_INPUTS.integrations,
      LIMITS.integrations.min,
      LIMITS.integrations.max,
    ),
  };
}

/** Normalises inputs coming from the UI (clamps numbers, forces store on for ecommerce sites). */
export function normalizeInputs(input: CalculatorInputs): CalculatorInputs {
  return {
    type: input.type,
    pages: clamp(Math.round(input.pages) || LIMITS.pages.min, LIMITS.pages.min, LIMITS.pages.max),
    languages: clamp(Math.round(input.languages) || 1, LIMITS.languages.min, LIMITS.languages.max),
    ecommerce: input.type === 'ecommerce' ? true : input.ecommerce,
    integrations: clamp(Math.round(input.integrations) || 0, LIMITS.integrations.min, LIMITS.integrations.max),
  };
}

/** Shareable query string for a set of inputs (no leading "?"). */
export function toQueryString(input: CalculatorInputs): string {
  const i = normalizeInputs(input);
  return new URLSearchParams({
    type: i.type,
    pages: String(i.pages),
    langs: String(i.languages),
    store: i.ecommerce ? '1' : '0',
    integrations: String(i.integrations),
  }).toString();
}

// ── Calculation ──────────────────────────────────────────────────────────────

export interface Range {
  min: number;
  max: number;
}
export interface CostResult {
  /** The single mid-point estimate in EGP, before rounding. */
  estimateEgp: number;
  ranges: Record<Currency, Range>;
}

const ROUND_TO: Record<Currency, number> = { EGP: 500, SAR: 50, AED: 50 };
const roundTo = (value: number, step: number) => Math.round(value / step) * step;

export function calculate(rawInput: CalculatorInputs, config: CalculatorConfig): CostResult {
  const input = normalizeInputs(rawInput);

  const extraPages = Math.max(0, input.pages - config.includedPages[input.type]);
  const storeAddon = input.ecommerce && input.type !== 'ecommerce' ? config.ecommerceAddon : 0;
  const subtotal =
    config.basePrices[input.type] +
    extraPages * config.extraPagePrice +
    storeAddon +
    input.integrations * config.integrationPrice;
  const estimateEgp = subtotal * (1 + ((input.languages - 1) * config.extraLanguagePercent) / 100);

  const range = (currency: Currency): Range => {
    const rate = currency === 'EGP' ? 1 : config.rates[currency];
    const min = roundTo(estimateEgp * config.rangeLow * rate, ROUND_TO[currency]);
    const max = roundTo(estimateEgp * config.rangeHigh * rate, ROUND_TO[currency]);
    return { min, max: Math.max(max, min) };
  };

  return { estimateEgp, ranges: { EGP: range('EGP'), SAR: range('SAR'), AED: range('AED') } };
}

export function formatMoney(value: number, locale: string): string {
  // Latin digits in both languages: stable between Node and browsers, so no hydration drift.
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-US', {
    maximumFractionDigits: 0,
  }).format(value);
}
